'use client';

import { useMemo, useCallback } from 'react';
import { useCurrencyStore } from '@/store/currencyStore';
import {
  convertAmountWithRates,
  getCurrencyCode,
} from '@/utils/currencyHelpers';

/**
 * useCurrency Hook
 *
 * Provides currency utilities for complex scenarios where you need more control
 * than the simple <CurrencyDisplay> component provides.
 *
 * Returns:
 * - currencyCode: Current currency code (e.g., 'INR', 'USD')
 * - currencySymbol: Current currency symbol (e.g., '₹', '$')
 * - targetCurrencyRate: Exchange rate for conversion
 * - convertPrice: Function to convert backend price to user's currency
 * - formatPrice: Function to format and convert a price with currency symbol
 *
 * Usage:
 * ```tsx
 * const { currencySymbol, convertPrice, formatPrice } = useCurrency();
 * const converted = convertPrice(100); // Backend $100 → ₹8300 for India
 * const formatted = formatPrice(100); // "₹8300" for India, "$100" for USA
 * ```
 */
export function useCurrency() {
  const {
    currencyCode,
    currencySymbol,
    targetCurrencyRate,
    exchangeRates,
    baseCurrency,
  } = useCurrencyStore();

  /**
   * Convert a price from backend currency to user's display currency
   */
  const convertPrice = useCallback(
    (
      amount: number,
      backendCurrency?: string,
      overrideRate?: number
    ): number => {
      const safeAmount =
        typeof amount === 'number' && !Number.isNaN(amount) ? amount : 0;
      const fromCurrency = backendCurrency
        ? getCurrencyCode(backendCurrency)
        : baseCurrency || 'USD';
      const toCurrency = currencyCode || baseCurrency || 'USD';

      let converted = convertAmountWithRates({
        amount: safeAmount,
        fromCurrency,
        toCurrency,
        exchangeRates,
        baseCurrency,
      });

      const currenciesDiffer = fromCurrency !== toCurrency;
      const needsFallback =
        currenciesDiffer &&
        (Number.isNaN(converted) || converted === safeAmount);

      if (needsFallback) {
        const base = baseCurrency || 'USD';
        const rate =
          typeof overrideRate === 'number' && overrideRate > 0
            ? overrideRate
            : targetCurrencyRate;

        if (rate && rate > 0) {
          if (fromCurrency === base) {
            converted = safeAmount * rate;
          } else if (toCurrency === base) {
            converted = safeAmount / rate;
          } else {
            const amountInBase =
              fromCurrency === base ? safeAmount : safeAmount / rate;
            converted =
              toCurrency === base ? amountInBase : amountInBase * rate;
          }
        } else {
          converted = safeAmount;
        }
      }

      return Number.isFinite(converted) ? converted : 0;
    },
    [baseCurrency, currencyCode, exchangeRates, targetCurrencyRate]
  );

  /**
   * Format a price with currency symbol (with conversion)
   * @param amount - The amount from backend (usually USD)
   * @param options - Formatting options
   */
  const formatPrice = useMemo(
    () =>
      (
        amount: number,
        options?: {
          showDecimals?: boolean;
          decimalPlaces?: number;
        },
        backendCurrency?: string,
        overrideRate?: number
      ): string => {
        const convertedAmount = convertPrice(
          amount,
          backendCurrency,
          overrideRate
        );
        const { showDecimals = true, decimalPlaces = 2 } = options || {};

        const formattedAmount = showDecimals
          ? convertedAmount.toFixed(decimalPlaces)
          : Math.round(convertedAmount).toString();

        return `${currencySymbol}${formattedAmount}`;
      },
    [convertPrice, currencySymbol]
  );

  return {
    currencyCode,
    currencySymbol,
    targetCurrencyRate,
    convertPrice,
    formatPrice,
  };
}
